import { useEffect, useState } from "react";
import { Search, Hash } from "lucide-react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../services/APIs/zulipServices";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";

const Channels = () => {
  const navigate = useNavigate();
  const [channelList, setChannelList] = useState<any[]>([]);

  // Fetch channels list
  const { data: channelsData, isLoading: isLoadingChannels } = useQuery({
    queryKey: ["zulip", "streams"],
    queryFn: async () => {
      const res = await ZulipServices.getChannelList();
      return res?.streams ?? [];
    },
  });

  useEffect(() => {
    if (channelsData?.length) {
      setChannelList(channelsData);
    } else {
      setChannelList([]);
    }
  }, [channelsData]);

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-lg font-bold text-primary md:text-xl">
                Channels
              </h1>
              <p className="text-xs text-muted-foreground">
                Group conversations and topics
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search channels..." className="pl-10" />
          </div>
        </div>

        {isLoadingChannels ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading channels...
          </div>
        ) : channelList.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No channels available
          </div>
        ) : (
          <div className="space-y-2">
            {channelList.map((channel: any) => {
              const channelId = channel.stream_id ?? channel.id;
              const channelName = channel.name ?? "Unknown Channel";
              const subscriberCount = channel.subscriber_count ?? 0;

              return (
                <Card
                  key={channelId}
                  className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
                  onClick={() =>
                    navigate(
                      `${CitycollabNavigationRoutes.CHANNELS}/${channelId}?channelId=${channelId}`
                    )
                  }
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                      <Hash className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <h3 className="font-medium">#{channelName}</h3>
                        {subscriberCount > 0 && (
                          <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                            {subscriberCount} members
                          </span>
                        )}
                      </div>
                      {channel.description && (
                        <p className="truncate text-sm text-muted-foreground">
                          {channel.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Channels;
